import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { server } from "..";
import Loader from "./Loader";
import ToolWorkspace from "../components/ToolWorkspace";

const Notemaking = () => {
  const [inputText, setInputText] = useState("");
  const [summaryOutput, setSummaryOutput] = useState("");
  const [notesOutput, setNotesOutput] = useState("");
  const [disable, setDisable] = useState(false);
  const [loader, setLoader] = useState(false);
  const controllerRef = useRef(null);

  const handleRequest = async (type) => {
    if (!inputText || inputText.length <= 20) {
      toast.error("Too short");
      return;
    }

    setDisable(true);
    setLoader(true);

    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();

    try {
      const endpoint = type === "summary" ? "summarize" : "note";

      const { data } = await axios.post(
        `${server}/tools/${endpoint}`,
        { name: inputText },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          signal: controllerRef.current.signal,
          timeout: 50000,
        }
      );

      if (type === "summary") setSummaryOutput(data.trying);
      else setNotesOutput(data.trying);

      toast.success(`${type === "summary" ? "Summary" : "Notes"} Done!`);
    } catch {
      toast.error("This AI model exceeds Render’s free plan limits.");
    } finally {
      setDisable(false);
      setLoader(false);
    }
  };

  const handleClear = () => {
    setInputText("");
    setSummaryOutput("");
    setNotesOutput("");
  };

  return (
    <ToolWorkspace
      label="Notes"
      title="Summary and Note Making"
      subtitle="Turn longer text into a short summary or study-ready notes."
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <label className="font-display text-sm font-semibold text-brand-forest">
            Your text
          </label>
          <textarea
            onChange={(e) => setInputText(e.target.value)}
            className="min-h-[280px] w-full resize-y rounded-xl border border-brand-forest/12 bg-white px-4 py-3 font-display text-[0.95rem] text-brand-forest outline-none transition placeholder:text-brand-muted/55 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
            placeholder="Paste the text you want summarized or turned into notes..."
            value={inputText}
          />
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => handleRequest("summary")}
              className="btn w-full disabled:cursor-not-allowed disabled:opacity-70"
              disabled={disable}
            >
              Summary
            </button>
            <button
              type="button"
              onClick={() => handleRequest("notes")}
              className="inline-flex w-full items-center justify-center rounded-full border border-brand-teal bg-brand-soft px-7 py-3 font-display text-[0.95rem] font-semibold text-brand-teal-dark transition hover:-translate-y-0.5 hover:bg-brand-teal hover:text-white disabled:cursor-not-allowed disabled:opacity-70"
              disabled={disable}
            >
              Notes
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex w-full items-center justify-center rounded-full border border-brand-forest/15 bg-white px-7 py-3 font-display text-[0.95rem] font-semibold text-brand-forest transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <label className="font-display text-sm font-semibold text-brand-forest">
            Results
          </label>
          {loader ? (
            <div className="flex min-h-[280px] w-full items-center justify-center rounded-xl border border-brand-forest/10 bg-brand-surface">
              <Loader />
            </div>
          ) : (
            <>
              <textarea
                disabled
                className="min-h-[140px] w-full resize-y rounded-xl border border-brand-forest/12 bg-brand-surface/50 px-4 py-3 font-display text-[0.95rem] text-brand-forest outline-none placeholder:text-brand-muted/55"
                placeholder="Summary"
                value={summaryOutput}
              />
              <textarea
                disabled
                className="min-h-[140px] w-full resize-y rounded-xl border border-brand-forest/12 bg-brand-surface/50 px-4 py-3 font-display text-[0.95rem] text-brand-forest outline-none placeholder:text-brand-muted/55"
                placeholder="Notes"
                value={notesOutput}
              />
            </>
          )}
        </div>
      </div>
    </ToolWorkspace>
  );
};

export default Notemaking;
